import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, ChevronDown, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const CALENDLY_URL = 'https://calendly.com/contact-bankkimedia/30min';

const PackagesPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const { t } = useTranslation();
  const hero = t('packages.hero', { returnObjects: true });
  const packages = t('packages.items', { returnObjects: true });
  const faqs = t('packages.faqs', { returnObjects: true });
  const bottom = t('packages.bottomCta', { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Packages & Pricing" />
      <Header />
      <main>
        <section className="pt-28 pb-12 md:pt-36 md:pb-16">
          <div className="container-custom text-center max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#c8ff00]/30 bg-[#c8ff00]/5 mb-8">
                <Clock className="w-4 h-4 text-[#c8ff00]" />
                <span className="text-[#c8ff00] text-sm font-bold uppercase tracking-wider">{hero.badge}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold font-['Syne'] leading-tight mb-4">{hero.title1}<br /><span className="text-[#c8ff00]">{hero.title2}</span></h1>
              <p className="text-white/50 text-lg max-w-xl mx-auto">{hero.desc}</p>
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="container-custom max-w-5xl">
            <div className="space-y-6">
              {packages.map((pkg, index) => (
                <motion.div key={pkg.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} data-testid={`package-${pkg.id}`}
                  className={`relative rounded-2xl p-6 md:p-8 border ${pkg.highlight ? 'border-[#c8ff00]/40 bg-[#c8ff00]/[0.03]' : 'border-white/10 bg-white/[0.02]'}`}>
                  {pkg.badge && <span className={`absolute -top-3 left-6 px-3 py-1 text-xs font-bold rounded-full ${pkg.highlight ? 'bg-[#c8ff00] text-black' : 'bg-white/10 text-white'}`}>{pkg.badge}</span>}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold font-['Syne'] text-white mb-1">{pkg.name}</h3>
                      <p className="text-gray-500 text-sm mb-5">{pkg.tagline}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {pkg.features.map((f) => <div key={f} className="flex items-center gap-2 text-gray-300 text-sm"><Check className="w-4 h-4 text-[#c8ff00] flex-shrink-0" />{f}</div>)}
                      </div>
                    </div>
                    <div className="md:text-right flex-shrink-0 md:min-w-[200px]">
                      <div className="mb-1">
                        {pkg.price !== 'Custom' && <span className="text-white/40 text-lg">$</span>}
                        <span className={`text-4xl md:text-5xl font-bold font-['Syne'] ${pkg.highlight ? 'text-[#c8ff00]' : 'text-white'}`}>{pkg.price}</span>
                        {pkg.period && <span className="text-gray-500 text-lg">{pkg.period}</span>}
                      </div>
                      <p className="text-gray-500 text-xs mb-4">{pkg.payment}</p>
                      {pkg.value !== 'Custom' && <p className="text-[#c8ff00]/60 text-xs mb-4">{t('packages.realValue')} {pkg.value}</p>}
                      <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-6 py-3 font-bold rounded-full transition-all text-sm ${pkg.highlight ? 'bg-[#c8ff00] text-black hover:bg-[#d4ff33]' : 'bg-white text-black hover:bg-gray-100'}`}>
                        {pkg.price === 'Custom' ? t('packages.talkToUs') : t('packages.getStarted')}<ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 border-t border-white/5">
          <div className="container-custom max-w-3xl text-center">
            <p className="text-gray-500 text-sm">
              {t('packages.note')}<br />
              {t('packages.notSure')} <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="text-[#c8ff00] underline underline-offset-2 hover:text-[#d4ff33]">{t('packages.bookFreeCall')}</a> {t('packages.andWeRecommend')}
            </p>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="container-custom max-w-3xl">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold font-['Syne'] text-white text-center mb-10">{t('packages.faqTitle')}</motion.h2>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border border-white/10 rounded-xl overflow-hidden bg-white/[0.02]">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} data-testid={`pkg-faq-${idx}`} className="w-full flex items-center justify-between p-5 text-left">
                    <span className="text-white font-medium pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === idx && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                        <p className="px-5 pb-5 text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 border-t border-white/5">
          <div className="container-custom max-w-3xl text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-5xl font-bold font-['Syne'] text-white mb-4">{bottom.title}</h2>
              <p className="text-gray-400 text-lg mb-8">{bottom.desc}</p>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-10 py-4 bg-[#c8ff00] text-black font-bold rounded-full text-lg hover:bg-[#d4ff33] transition-colors">
                {bottom.cta}<ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-white/30 text-sm mt-4">{bottom.sub}</p>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PackagesPage;
